"use client";

import * as React from "react";
import { Inbox } from "lucide-react";
import { cn, EmptyState, type EmptyStateProps } from "@aio/platform-ui";

/**
 * Props for {@link EmptyTable}.
 *
 * Mirrors {@link EmptyStateProps} from `@aio/platform-ui` but with sensible
 * defaults tuned for a "no rows" table state — the icon defaults to a
 * lucide `Inbox` and the title falls back to a localizable English string.
 *
 * Two render modes:
 * - **Standalone** (default): emits the `EmptyState` panel inside a wrapping
 *   `<div>` so callers can drop it next to or instead of a table.
 * - **In-table** (when `colSpan` is provided): emits a single `<tr><td>` so
 *   the empty state can sit inside an otherwise-empty `<tbody>` without
 *   breaking the table's column structure.
 */
export interface EmptyTableProps
  extends Omit<EmptyStateProps, "title"> {
  /**
   * Override the empty-state title. Defaults to "No results yet."
   *
   * The `title` is required by {@link EmptyState} but the wrapper provides
   * a default so callers without a custom message don't need to pass one.
   */
  title?: string;
  /**
   * If provided, render the empty state as a `<tr><td colSpan={colSpan}>`
   * suitable for slotting into an empty `<tbody>`. The number must match
   * the visible column count (including the selection column when present)
   * so the cell spans the full table width.
   *
   * If omitted, the wrapper renders a standalone `<div>` and the caller is
   * responsible for placing it outside the `<table>` element.
   */
  colSpan?: number;
  /** Test id forwarded to the outermost element (`<tr>` or `<div>`). */
  "data-testid"?: string;
}

/**
 * Empty-state wrapper for the table engine.
 *
 * When the data array is empty (or the dataSource resolved to zero rows),
 * the engine renders this component instead of an empty `<tbody>`. The
 * `colSpan` mode keeps the rendered DOM inside the `<table>` element so
 * surrounding chrome (sticky header, footer, keyboard focus management)
 * keeps working uniformly.
 */
export function EmptyTable({
  title = "No results yet.",
  description,
  icon = <Inbox aria-hidden="true" />,
  action,
  colSpan,
  className,
  "data-testid": dataTestId = "empty-table",
}: EmptyTableProps): React.JSX.Element {
  // The `EmptyState` body ignores className strictness — we style spacing on
  // the outer wrapper so both modes feel consistent regardless of how big
  // the surrounding table is.
  const panel = (
    <EmptyState
      title={title}
      {...(description !== undefined ? { description } : {})}
      {...(icon !== undefined ? { icon } : {})}
      {...(action !== undefined ? { action } : {})}
      // Strip the dashed border in colSpan mode so the empty state visually
      // sits inside the table's existing border, not beside another one.
      className={cn(
        colSpan !== undefined ? "border-0 bg-transparent" : undefined,
        className,
      )}
    />
  );

  if (colSpan !== undefined) {
    return (
      <tr data-testid={dataTestId}>
        <td colSpan={colSpan} className="p-0">
          {panel}
        </td>
      </tr>
    );
  }

  return (
    <div className="w-full" data-testid={dataTestId}>
      {panel}
    </div>
  );
}
